import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { User, Phone, Mail, Key, LogOut, Edit2, ChevronLeft, Check, X, CreditCard, Image as ImageIcon, Building2, Hash } from 'lucide-react';
import ImageUploadModal from '../../user/account/ImageUploadModal';

const PERUVIAN_BANKS = [
  'Banco de Crédito del Perú (BCP)',
  'BBVA',
  'Scotiabank',
  'Interbank',
  'Banco de la Nación'
];

function MyAccount({ userId, onLogout, setActiveComponent }) {
  const [user, setUser] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalType, setImageModalType] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, [userId]);

  async function fetchUserData() {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/user_edit/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUser(response.data.user);
      setError('');
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Error al cargar los datos del usuario');
    } finally {
      setLoading(false);
    }
  }

  function handleEditClick(field, currentValue) {
    setEditingField(field);
    setEditingValue(currentValue);
  }

  function handleCancelEdit() {
    setEditingField(null);
    setEditingValue('');
  }

  async function handleSubmit() {
    const requestBodyKeyMap = {
      username: 'username',
      email: 'email',
      phoneNumber: 'phone_number',
      password: 'password_hash',
      bankAccount: 'bank_account',
      bankUsername: 'name_on_bank_account',
      interBankAccount: 'interbancario_bank_account',
      bank: 'bank_name'
    };
    
    const requestBodyKey = requestBodyKeyMap[editingField];
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/user_edit/${userId}`,
        { [requestBodyKey]: editingValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUser(response.data.user);
      setEditingField(null);
      setEditingValue('');
    } catch (error) {
      console.error('Error updating user:', error);
      setError('Error al actualizar la información');
    }
  }

  function handleOpenImageModal(type) {
    setImageModalType(type);
    setImageModalOpen(true);
  }

  function handleCloseImageModal() {
    if (!imageUploading) {
      setImageModalOpen(false);
      setImageModalType(null);
    }
  }

  async function handleImageUpload(imageData) {
    const token = localStorage.getItem('token');
    setImageUploading(true);
    setError('');

    try {
      const fieldName = imageModalType === 'profile' ? 'profile_image' : 'background_image';
      
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/user_edit/${userId}`,
        { [fieldName]: imageData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setUser(response.data.user);
      setImageModalOpen(false);
      setImageModalType(null);
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Error al subir la imagen. Por favor intenta de nuevo.');
    } finally {
      setImageUploading(false);
    }
  }

  async function handleImageDelete() {
    const token = localStorage.getItem('token');
    setImageUploading(true);
    setError('');

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/user_edit/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: { image_type: imageModalType }
        }
      );
      
      // Update local state
      setUser(prev => ({
        ...prev,
        [imageModalType === 'profile' ? 'profile_image' : 'background_image']: null
      }));
      
      setImageModalOpen(false);
      setImageModalType(null);
    } catch (error) {
      console.error('Error deleting image:', error);
      setError('Error al eliminar la imagen. Por favor intenta de nuevo.');
    } finally {
      setImageUploading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#B1336E] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-600 font-montserrat">Cargando...</p>
        </div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 max-w-md">
          <p className="text-red-700 font-montserrat">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Fixed Header with Back Button */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setActiveComponent('OptionsAdmin')}
            className="flex items-center gap-2 text-slate-700 hover:text-[#B1336E] transition-colors duration-200 font-montserrat"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm sm:text-base">Volver</span>
          </button>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8 sm:py-8">
          {/* Profile Header with Images */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
            {/* Background Image Section */}
            <div className="relative h-32 sm:h-40 bg-gradient-to-r from-[#B1336E] to-[#E6B43C]">
              {user.background_image && (
                <img
                  src={user.background_image}
                  alt="Background"
                  className="w-full h-full object-cover"
                />
              )}
              <button
                onClick={() => handleOpenImageModal('background')}
                className="absolute top-3 right-3 bg-white/90 hover:bg-white text-[#B1336E] p-2 rounded-lg shadow-lg transition-all"
                aria-label="Editar imagen de fondo"
              >
                <ImageIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Profile Image Section */}
            <div className="relative px-6 pb-6">
              <div className="flex flex-col items-center -mt-12">
                <div className="relative">
                  <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 border-white shadow-lg overflow-hidden bg-gradient-to-br from-[#B1336E] to-[#E6B43C]">
                    {user.profile_image ? (
                      <img
                        src={user.profile_image}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-12 h-12 text-white" />
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleOpenImageModal('profile')}
                    className="absolute bottom-0 right-0 bg-white hover:bg-slate-50 text-[#B1336E] p-2 rounded-full shadow-lg border-2 border-white transition-all"
                    aria-label="Editar imagen de perfil"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                
                <h1 className="mt-4 text-2xl sm:text-3xl font-extrabold text-[#B1336E] font-montserrat">
                  Mi Cuenta
                </h1>
                <p className="mt-2 text-sm sm:text-base text-slate-600 font-montserrat">
                  Gestiona tu información personal
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm text-red-700 font-montserrat">{error}</p>
            </div>
          )}

          {/* User Details */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="divide-y divide-slate-100">
              {/* Username */}
              <ProfileField
                icon={<User className="w-5 h-5" />}
                label="Nombre de usuario"
                value={user.username}
                field="username"
                editingField={editingField}
                editingValue={editingValue}
                onEdit={handleEditClick}
                onSubmit={handleSubmit}
                onCancel={handleCancelEdit}
                onChange={setEditingValue}
              />

              {/* Phone */}
              <ProfileField
                icon={<Phone className="w-5 h-5" />}
                label="Teléfono"
                value={user.phone_number}
                field="phoneNumber"
                editingField={editingField}
                editingValue={editingValue}
                onEdit={handleEditClick}
                onSubmit={handleSubmit}
                onCancel={handleCancelEdit}
                onChange={setEditingValue}
                type="tel"
              />

              {/* Email */}
              <ProfileField
                icon={<Mail className="w-5 h-5" />}
                label="Correo electrónico"
                value={user.email}
                field="email"
                editingField={editingField}
                editingValue={editingValue}
                onEdit={handleEditClick}
                onSubmit={handleSubmit}
                onCancel={handleCancelEdit}
                onChange={setEditingValue}
                type="email"
              />

              {/* Name on Bank Account */}
              <ProfileField
                icon={<User className="w-5 h-5" />}
                label="Nombre en cuenta bancaria"
                value={user.name_on_bank_account || 'No configurado'}
                field="bankUsername"
                editingField={editingField}
                editingValue={editingValue}
                onEdit={handleEditClick}
                onSubmit={handleSubmit}
                onCancel={handleCancelEdit}
                onChange={setEditingValue}
                placeholder="Ej: Juan Pérez García"
              />

              {/* Bank Name (Dropdown) */}
              <ProfileField
                icon={<Building2 className="w-5 h-5" />}
                label="Banco"
                value={user.bank_name || 'No configurado'}
                field="bank"
                editingField={editingField}
                editingValue={editingValue}
                onEdit={handleEditClick}
                onSubmit={handleSubmit}
                onCancel={handleCancelEdit}
                onChange={setEditingValue}
                inputType="select"
                options={PERUVIAN_BANKS}
              />

              {/* Bank Account Number */}
              <ProfileField
                icon={<CreditCard className="w-5 h-5" />}
                label="Número de cuenta bancaria"
                value={user.bank_account || 'No configurado'}
                field="bankAccount"
                editingField={editingField}
                editingValue={editingValue}
                onEdit={handleEditClick}
                onSubmit={handleSubmit}
                onCancel={handleCancelEdit}
                onChange={setEditingValue}
                placeholder="Ej: 19412345678901"
                type="text"
              />

              {/* Interbank Account (CCI) */}
              <ProfileField
                icon={<Hash className="w-5 h-5" />}
                label="Cuenta interbancaria (CCI)"
                value={user.interbancario_bank_account || 'No configurado'}
                field="interBankAccount"
                editingField={editingField}
                editingValue={editingValue}
                onEdit={handleEditClick}
                onSubmit={handleSubmit}
                onCancel={handleCancelEdit}
                onChange={setEditingValue}
                placeholder="Ej: 00219412345678901234"
                type="text"
              />

              {/* Password */}
              <ProfileField
                icon={<Key className="w-5 h-5" />}
                label="Contraseña"
                value="••••••••"
                field="password"
                editingField={editingField}
                editingValue={editingValue}
                onEdit={() => handleEditClick('password', '')}
                onSubmit={handleSubmit}
                onCancel={handleCancelEdit}
                onChange={setEditingValue}
                type="password"
                isPassword
              />
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer with Logout Button */}
      <div className="flex-shrink-0 bg-white border-t border-slate-200 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <button
            onClick={onLogout}
            className="w-full bg-white hover:bg-red-50 border-2 border-red-200 hover:border-red-300 text-red-600 font-semibold py-3 px-4 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-sm font-montserrat"
          >
            <LogOut className="w-5 h-5" />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </div>

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={imageModalOpen}
        onClose={handleCloseImageModal}
        onUpload={handleImageUpload}
        onDelete={handleImageDelete}
        currentImage={imageModalType === 'profile' ? user?.profile_image : user?.background_image}
        imageType={imageModalType}
        loading={imageUploading}
      />
    </div>
  );
}

function ProfileField({
  icon,
  label,
  value,
  field,
  editingField,
  editingValue,
  onEdit,
  onSubmit,
  onCancel,
  onChange,
  type = 'text',
  isPassword = false,
  placeholder = '',
  inputType = 'text',
  options = []
}) {
  const isEditing = editingField === field;

  return (
    <div className="p-4 sm:p-6 hover:bg-slate-50 transition-colors duration-150">
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#B1336E]/10 flex items-center justify-center text-[#B1336E] mt-1">
          {icon}
        </div>
        
        <div className="flex-1 min-w-0">
          <label className="block text-xs sm:text-sm font-medium text-slate-500 mb-1 font-montserrat">
            {label}
          </label>
          
          {isEditing ? (
            <div className="space-y-3">
              {inputType === 'select' ? (
                <select
                  value={editingValue}
                  onChange={(e) => onChange(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-[#B1336E] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 text-sm sm:text-base font-montserrat bg-white"
                  autoFocus
                >
                  <option value="">Seleccione un banco</option>
                  {options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={type}
                  value={editingValue}
                  onChange={(e) => onChange(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-[#B1336E] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 text-sm sm:text-base font-montserrat"
                  placeholder={placeholder || (isPassword ? 'Nueva contraseña' : `Ingrese ${label.toLowerCase()}`)}
                  autoFocus
                />
              )}
              <div className="flex gap-2">
                <button
                  onClick={onSubmit}
                  className="flex-1 bg-[#67A43C] hover:bg-[#67A43C]/90 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 text-sm font-montserrat"
                >
                  <Check className="w-4 h-4" />
                  <span className="hidden sm:inline">Guardar</span>
                </button>
                <button
                  onClick={onCancel}
                  className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 text-sm font-montserrat"
                >
                  <X className="w-4 h-4" />
                  <span className="hidden sm:inline">Cancelar</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm sm:text-base text-slate-900 font-medium truncate font-montserrat">
                {value}
              </p>
              <button
                onClick={() => onEdit(field, isPassword ? '' : value)}
                className="flex-shrink-0 p-2 text-[#B1336E] hover:bg-[#B1336E]/10 rounded-lg transition-colors duration-200"
                aria-label={`Editar ${label}`}
              >
                <Edit2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MyAccount;